import * as React from 'react';
import { Customer } from 'types/Customer';
import { ControlledAssignButton } from './ControlledAssignButton';

type PromptState = { action: 'prompt'; customer: null };
type SelectState = { action: 'selecting'; customer: Customer | null };
type TagState = { action: 'chosen'; customer: Customer };

/**
 * Keeping all the state together like this avoids a bunch of intermediate states that would occur
 * if we were to set values one at a time in separate useState calls
 */
export type State = PromptState | SelectState | TagState;

export interface Props {
  initialCustomer?: Customer;
  onSelect?: (customer: Customer | null) => void;
  onIsOpen?: (open: boolean) => void;
  /** Additional classes to apply to the picker */
  className?: string;
}

/**
 * This is the fully self-controlled version of the ControlledAssignButton
 */
export const UncontrolledAssignButton: React.FC<Props> = (props) => {
  const { initialCustomer, onSelect, onIsOpen } = props;

  const [state, setState] = React.useState<State>(
    initialCustomer
      ? { action: 'chosen', customer: initialCustomer }
      : { action: 'prompt', customer: null }
  );

  const handleIsOpen = (open: boolean) => {
    setState((s) => {
      if (open) {
        return {
          action: 'selecting',
          customer: s.customer,
        };
      }

      if (s.customer) {
        return { action: 'chosen', customer: s.customer };
      }
      return { action: 'prompt', customer: null };
    });

    onIsOpen?.(open);
  };

  const handleSetCustomer = (customer: Customer | null, close?: boolean) => {
    if (customer) {
      setState({ action: close ? 'chosen' : 'selecting', customer });
    } else {
      setState({ action: close ? 'prompt' : 'selecting', customer });
    }

    onSelect?.(customer);
    if (close && onIsOpen) {
      onIsOpen(false);
    }
  };

  return (
    <ControlledAssignButton
      isOpen={state.action === 'selecting'}
      setIsOpen={handleIsOpen}
      customer={state.customer}
      setCustomer={handleSetCustomer}
      className={props.className}
    />
  );
};
UncontrolledAssignButton.displayName = 'CustomerTag.AssignButton';
